import React, { useEffect, useState } from "react";
import {
  Slider,
  Button,
  Tag,
  Space,
  Typography,
  Spin,
} from "antd";
import {
  SaveOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CodeOutlined,
  SolutionOutlined,
  ReadOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  fetchATSScoringWeights,
  updateATSScoringWeightsThunk,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  selectATSScoringWeights,
  selectATSScoringWeightsLoading,
} from "../../../../core/data/redux/recruitment/selectors";
import { notify } from "../../../../services/toast.config";

const { Text, Title, Paragraph } = Typography;

export interface ATSScoringWeightsMatch {
  skills_match: number;
  experience_match: number;
  education_match: number;
  keyword_match: number;
}

const DEFAULT_WEIGHTS: ATSScoringWeightsMatch = {
  skills_match: 40, experience_match: 30, education_match: 15, keyword_match: 15,
};

const SECTIONS = [
  { key: "skills_match", label: "Skills Matching", color: "#1677ff", bg: "#EFF6FF", icon: <CodeOutlined />, description: "How closely the candidate's skills match the job requirements." },
  { key: "experience_match", label: "Experience", color: "#13c2c2", bg: "#E6FFFB", icon: <SolutionOutlined />, description: "Years and relevance of prior work experience." },
  { key: "education_match", label: "Education", color: "#722ed1", bg: "#F9F0FF", icon: <ReadOutlined />, description: "Degree level and field of study vs. what the role requires." },
  { key: "keyword_match", label: "Keywords", color: "#fa8c16", bg: "#FFF7E6", icon: <TagsOutlined />, description: "Presence of key terms and phrases from the job description." },
] as const;

const ATSScoringWeightsEditor: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const storedWeights = useSelector(selectATSScoringWeights);
  const loading = useSelector(selectATSScoringWeightsLoading);
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);

  const [weights, setWeights] = useState<ATSScoringWeightsMatch>(DEFAULT_WEIGHTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeCompany?.company_id) dispatch(fetchATSScoringWeights());
  }, [dispatch, activeCompany?.company_id, activeCompany?.company_type]);

  useEffect(() => {
    if (storedWeights) setWeights(storedWeights as any as ATSScoringWeightsMatch);
  }, [storedWeights]);

  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const isValid = total === 100;

  const handleChange = (key: string, value: number) =>
    setWeights((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!isValid) { notify.warn("Total weights must equal 100%"); return; }
    setSaving(true);
    try {
      await dispatch(updateATSScoringWeightsThunk(weights)).unwrap();
      notify.success("ATS scoring weights updated");
    } catch (error: any) {
      notify.error(error?.data?.message || error || "Failed to update weights");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "80px 0" }}><Spin size="large" /></div>;
  }

  return (
    <>
      
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Title level={5} style={{ margin: 0, fontWeight: 600 }}>ATS Scoring Weights</Title>
          <Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: 13 }}>
            Control how much each criterion contributes to a candidate's overall match score.
            Weights must add up to exactly 100%.
          </Paragraph>
        </div>
        <Space>
          <Tag
            color={isValid ? "success" : "error"}
            icon={isValid ? <CheckCircleOutlined /> : <WarningOutlined />}
            style={{ fontSize: 13, padding: "4px 12px" }}>
            Total: {total}%
          </Tag>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!isValid}>
            Save
          </Button>
        </Space>
      </div>

      
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
        {SECTIONS.map(({ key, label, color, bg, icon, description }) => {
          const value = weights[key as keyof ATSScoringWeightsMatch];
          return (
            <div
              key={key}
              style={{
                background: "#fff",
                border: "1px solid #f0f0f0",
                borderTop: `3px solid ${color}`,
                borderRadius: 10,
                padding: 20,
              }}>

              
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 8,
                    background: bg, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, color,
                  }}>
                    {icon}
                  </div>
                  <Text strong style={{ fontSize: 14 }}>{label}</Text>
                </div>
                <Text strong style={{ fontSize: 26, color, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {value}%
                </Text>
              </div>

              
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                {description}
              </Text>

              
              <Slider
                min={0} max={100}
                value={value}
                onChange={(val) => handleChange(key, val)}
                styles={{
                  track: { background: color },
                  rail: { background: "#f0f0f0" },
                }}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ATSScoringWeightsEditor;